import { FC, useEffect, useState } from "react";
import classes from './financeWidget.module.scss'
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { FEATURES, hasFeature, useMyActions } from "../../../entities/my";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { useAppSelector } from "../../../app/store/store";
import { useGlobalLoadingActions } from "../../../entities/globalLoading";
import { adminService, IAdminFinanceByTgAdmins } from "../../../entities/admin";
import { getDateUTC } from "../../../shared/lib/helpers/getDateUTC";
import { Calendar } from "../../../shared/ui/calendar";
import { WorkTgFilter } from "../../workTgFilter";
import { MyButton } from "../../../shared/ui/button";

const metricValue = (value?: string | number | null) => value ?? '0'

const decimalValue = (value?: string | number | null) => {
    const numberValue = Number(value ?? 0)
    if(Number.isNaN(numberValue)){
        return '0,0'
    }

    return numberValue.toFixed(1).replace('.', ',')
}

const moneyValue = (value?: string | number | null) => `${decimalValue(value)} ₽`

const hasValue = (value?: string | number | null) => {
    const numberValue = Number(value ?? 0)
    return !Number.isNaN(numberValue) && numberValue !== 0
}

export const FinanceWidget: FC = () => {

    const {my} = useAppSelector(s => s.myReducer)
    const canFinanceByWorkTgs = hasFeature(my, FEATURES.FINANCE.BY_WORK_TGS_LIST)
        && hasFeature(my, FEATURES.WORK_TGS.LIST)
    const canDownloadFinanceDetails = hasFeature(my, FEATURES.FINANCE.DETAILS_DOWNLOAD)
    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsLoading} = useGlobalLoadingActions()
    const [finance, setFinance] = useState<IAdminFinanceByTgAdmins | null>(null)
    const [workTgIds, setWorkTgIds] = useState<number[]>([])
    const [isDownloading, setIsDownloading] = useState(false)
    
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    
    const getData = async (startDate: string, endDate: string) => {
        try{
            setIsLoading(true)
            if(workTgIds.length){
                const financeRes = await adminService.getFinanceByTgAdmins(startDate, endDate, my.id, workTgIds)
                setFinance(financeRes)
            }
            else{
                const financeRes = await adminService.getFinance(startDate, endDate)
                setFinance(financeRes)
            }
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при получении данных о финансах', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    const downloadFile = (blob: Blob, fileName: string) => {
        const a = document.createElement('a')
        const url = URL.createObjectURL(blob)
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 0)
    }

    const downloadDetails = async () => {
        if(!startDate || !endDate || !canDownloadFinanceDetails || isDownloading){
            return
        }

        try{
            setIsDownloading(true)
            const {blob, fileName} = await adminService.downloadFinanceDetails(getDateUTC(startDate), getDateUTC(endDate), workTgIds)
            downloadFile(blob, fileName)
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                if(e.status === 403){
                    setGlobalMessage({message: 'Нет прав на выгрузку детализации финансов', type: 'error'})
                    return
                }

                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при скачивании детализации финансов', type: 'error'})
            }
        }
        finally{
            setIsDownloading(false)
        }
    }
    
    const setDate = (startDate: Date | null, endDate: Date | null) => {
        setStartDate(startDate)
        setEndDate(endDate)
        if(startDate && endDate){
            getData(getDateUTC(startDate), getDateUTC(endDate))
        }
        if(!startDate && !endDate){
            setFinance(null)
        }
    }

    useEffect(() => {
        if(startDate && endDate && canFinanceByWorkTgs){
            getData(getDateUTC(startDate), getDateUTC(endDate))
        }
    }, [workTgIds, startDate, endDate, canFinanceByWorkTgs])

    return (
        <section className={classes.container}>
            <section className={classes.topRow}>
                <section className={classes.calendar}>
                    <Calendar onDateRangeSelect={setDate} />
                </section>
                {
                    canFinanceByWorkTgs
                        &&
                    <section className={classes.selectedWorkTgs}>
                        <WorkTgFilter
                            setWorkTgIds={setWorkTgIds}
                        />
                    </section>
                }
                {
                    canDownloadFinanceDetails
                        &&
                    <section className={classes.download}>
                        <MyButton
                            onClick={downloadDetails}
                            isLoading={isDownloading}
                            disabled={!startDate || !endDate}
                        >
                            Скачать детализацию XLSX
                        </MyButton>
                    </section>
                }
            </section>
            {
                finance
                    &&
                <section className={classes.data}>
                    <section className={classes.section}> 
                        <section className={classes.subtitle}>Общее</section>
                        {hasValue(finance.profit) && <span>Прибыль: {moneyValue(finance.profit)}</span>}
                        {hasValue(finance.cash_flow) && <span>Валовая прибыль (выручка): {moneyValue(finance.cash_flow)}</span>}
                        {hasValue(finance.debt) && <span>Дебиторская задолженность: {moneyValue(finance.debt)}</span>}
                        {hasValue(finance.transactions_count) && <span>Кол-во транзакций: {metricValue(finance.transactions_count)}</span>}
                        {hasValue(finance.confirmed_transactions_sum) && <span>Подтвержденные транзакции: {moneyValue(finance.confirmed_transactions_sum)}</span>}
                        {hasValue(finance.all_transactions_sum) && <span>Все транзакции: {moneyValue(finance.all_transactions_sum)}</span>}
                    </section>
                    <section className={classes.section}>
                        <section className={classes.subtitle}>Исполнители</section>
                        {hasValue(finance.salary) && <span>Общее вознаграждение: {moneyValue(finance.salary)}</span>}
                        {hasValue(finance.hours) && <span>Общее количество часов: {decimalValue(finance.hours)}</span>}
                        {hasValue(finance.lessons_count) && <span>Кол-во уроков: {metricValue(finance.lessons_count)}</span>}
                        {hasValue(finance.students_count) && <span>Кол-во учеников: {metricValue(finance.students_count)}</span>}
                    </section>
                </section>
            }
        </section>
    )
}
